import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AuthService } from '@app/services/auth.service';
import { Firestore } from '@angular/fire/firestore';
import { User } from '@app/models/user';
import { Define } from '@app/models/define';
import { Utility } from '@app/models/utility';
import { UserEx, UserService } from '@app/services/user.service';
import { ReportRecord } from 'src/app/models/report';
import { DatePipe } from '@angular/common';
import { TimeStamp } from '@app/models/report';
import { DataSource } from '@angular/cdk/collections';
import { Observable, BehaviorSubject } from 'rxjs';
import { SelectTimeDialogComponent } from '@app/pages/select-time-dialog/select-time-dialog.component';
import { addDoc, collection, doc, Timestamp, updateDoc } from 'firebase/firestore';

export interface TimeStampChip {
  actual: boolean;
  date: Date;
  dateText: string;
  disable: boolean;
}

export interface DialogData {
  row: ReportRecord;
}

export class RequestInfoSetRecord {
  constructor( public title: string, public actualStamp: string,
    public changeStamp: Date, public reject: boolean ) {}
}

export class RequestInfoSet extends DataSource<RequestInfoSetRecord> {
  data: BehaviorSubject<RequestInfoSetRecord[]>;
  constructor(public date: Date, public dateText: string, public timeStamps: Array<TimeStamp>, public changeStamps: Array<Date>,
    public approverUid: string, public cause: string, public rejects: Array<boolean>, public records: Array<RequestInfoSetRecord>) {
      super();
      this.data = new BehaviorSubject<RequestInfoSetRecord[]>(records);
}

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<RequestInfoSetRecord[]> {
    return this.data;
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() { }
}



@Component({
  selector: 'app-request-dialog',
  templateUrl: './request-dialog.component.html',
  styleUrls: ['./request-dialog.component.css']
})
export class RequestDialogComponent implements OnInit {
  date: Date;
  approvers: UserEx[];
  kinds = Define.RequestKinds;
  approverUid: string;
  submitting: boolean;
  sourceDateTexts: string[];
  minDateTime: Date;
  maxDateTime: Date;

  arriveTimeStamps = new Array<TimeStampChip>(0);
  leaveTimeStamps = new Array<TimeStampChip>(0);
  temporaryLeaveTimeStamps = new Array<TimeStampChip>(0);
  reenterTimeStamps = new Array<TimeStampChip>(0);
  removable = true;
  selectable = true;
  actualReport = new Array<string>(4).fill('-');
  previewDate: Date[][] = [[], [], [], []];
  preview = new Array<string>(4).fill('-');
  editable = false;

  isMobile = false;

  keySet = new RequestInfoSet(new Date(), '', new Array(), new Array(),  '', '', new Array(), new Array<RequestInfoSetRecord>());
  user: User;
  displayedColumns = ['kind', 'actual', 'change', 'reject'];
  constructor(
    private firestore: Firestore,
    public auth: AuthService,
    private userService: UserService,
    private dialogRef: MatDialogRef<RequestDialogComponent>,
    private datePipe: DatePipe,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DialogData) {
      this.sourceDateTexts = new Array<string>(10);
      this.keySet.rejects = new Array<boolean>(10);
  }

  async ngOnInit() {
    // モバイル端末判定
    const regexp = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    this.isMobile = window.navigator.userAgent.search(regexp) !== -1;
    this.approvers = new Array();
    this.user = await this.auth.getUser();
    this.keySet.date = this.data.row.date;
    this.keySet.timeStamps = Object.assign([], this.data.row.sourceStamps);
    const dayOfWeek = ' (' + ['日', '月', '火', '水', '木', '金', '土'][this.keySet.date.getDay()] + ')';
    this.keySet.dateText = this.datePipe.transform(this.keySet.date, 'yyyy/MM/dd') + dayOfWeek;
    // 承認者権限の場合は管理職権限のみを表示する。
    const managers: UserEx[] = await this.userService.getFilteredUsers(Define.Authority.Manager);
    let users: UserEx[];
    if (this.user.authority === Define.Authority.Normal) {
      // 一般職は承認者+管理職
      const approvers = await this.userService.getFilteredUsers(Define.Authority.Approver);
      users = approvers.concat(managers);
    } else {
      // 承認者は管理職のみ
      users = managers;
    }
    // const targetAuthority =
    //   this.user.authority === Define.Authority.Normal ? Define.Authority.Approver as number : Define.Authority.Manager as number;
    // const users = await this.userService.getFilteredUsers(targetAuthority);
    this.approvers = users.filter(u => u.uid !== this.user.uid).sort((a, b) => a.teamName < b.teamName ? -1 : 1); // 組織名称の昇順
    this.submitting = false;
    // this.keySet.timeStamps.forEach( ts => this.sourceDateTexts.push(Utility.convertDateToMinite(ts.stamp)));
    this.sourceDateTexts[0] = Utility.convertDateToDateMinite(this.data.row.cells[0].rawDate[0]);
    // 最遅打刻 array.slice(-1)[0]で最終要素 typescript@4.6.1から array.at(-1)が使用可能
    this.sourceDateTexts[1] = Utility.convertDateToDateMinite(this.data.row.cells[1].rawDate.slice(-1)[0]);
    // 実装方法が気に入らないが、とりあえず動く。データ構造で本来は吸収すべき内容だが、要求仕様がふらつくので正規化すると手戻りが大きくなりそうなので
    // この変なやり方で止めておく。
    for (let index = 0; index < 4; index++) {
      this.sourceDateTexts[(index * 2) + 2] = Utility.convertDateToDateMinite(this.data.row.cells[2].rawDate[index]);
      this.sourceDateTexts[(index * 2) + 3] = Utility.convertDateToDateMinite(this.data.row.cells[3].rawDate[index]);
    }

    const records = new Array<RequestInfoSetRecord>();
    const titles = ['出社', '退社', '外出1', '戻り1', '外出2', '戻り2', '外出3', '戻り3', '外出4', '戻り4'];
    for (let index = 0; index < 10; index++) {
      const r = new RequestInfoSetRecord(titles[index], this.sourceDateTexts[index], null, false);
      records.push(r);

    }
    this.keySet.data = new BehaviorSubject<RequestInfoSetRecord[]>(records);

    // console.log(this.keySet.data);
    const d = this.keySet.date;
    // 当該日の全日AM0時0分～翌日23時59分までを入力可能範囲とする。
    // 変則夜勤などでも入力できるようにするため。
    this.minDateTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    this.minDateTime.setDate(this.minDateTime.getDate() - 1);
    this.maxDateTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 0);
    this.maxDateTime.setDate(this.maxDateTime.getDate() + 1);

    const dateDiff = ['前', '', '翌'];
    if (this.keySet.timeStamps !== undefined) {
      this.keySet.timeStamps.forEach( timeStamp => {
        const dateDiffIndex = Utility.diffDay(this.keySet.date, timeStamp.stamp) + 1;
        if (this.actualReport[timeStamp.kind] === '-') {
          this.actualReport[timeStamp.kind] = Utility.convertDateToMinite(timeStamp.stamp);
        } else {
          this.actualReport[timeStamp.kind] += '\n' + Utility.convertDateToMinite(timeStamp.stamp);
        }
        if (timeStamp.kind === 2) {
          this.temporaryLeaveTimeStamps.push({
            actual: true,
            date: timeStamp.stamp,
            dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(timeStamp.stamp),
            disable: false
          });
        }
        if (timeStamp.kind === 3) {
          this.reenterTimeStamps.push({
            actual: true,
            date: timeStamp.stamp,
            dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(timeStamp.stamp),
            disable: false
          });
        }
      });
    }

    // DBに記憶している承認者を読み込む
    if (this.user.approverUid !== undefined) {
      this.approverUid = this.user.approverUid;
    }
    // 最近の申請データの読み込み (ユーザーの入力負荷低減)
    const preRequest = this.data.row.request;
    // 旧申請画面で行った申請データを読み込んでも完全に復元できないため制限
    if (preRequest !== undefined && preRequest.dataVersion >= 3) {
      this.approverUid = preRequest.approverUid;
      this.keySet.cause = preRequest.cause;

      preRequest.ejectFlags.forEach((e, index) => {
        if (e) {
          if (index === 0 || index === 1) {
            // 出社 退社の取消は行われない仕様のはず
            console.error('ejectFlag abnormal value');
          } else if (index % 2 === 0) {
            // 外出
            this.temporaryLeaveTimeStamps[(index - 2) / 2].disable = true;
            this.keySet.data.subscribe(r => {
              r[index].reject = true;
            });
          } else if (index % 2 === 1) {
            // 戻り
            this.reenterTimeStamps[(index - 3) / 2].disable = true;
            this.keySet.data.subscribe(r => {
              r[index].reject = true;
            });
          }
        }
      });

      preRequest.changeStamps.forEach((stamp, index) => {
        if (stamp !== null && stamp !== undefined) {
          // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
          const jstDate = Utility.convertLocalTimeToJstDate(stamp.toDate());

          const dateDiffIndex = Utility.diffDay(this.keySet.date, jstDate) + 1;
          if (index === 0) {
            // 出社
            this.arriveTimeStamps = [{
              actual: false,
              date: jstDate,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(jstDate),
              disable: false
            }];
            this.keySet.data.subscribe(r => {
              r[0].changeStamp = jstDate;
            });
          } else if (index === 1) {
            // 退社
            this.leaveTimeStamps = [{
              actual: false,
              date: jstDate,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(jstDate),
              disable: false
            }];
            this.keySet.data.subscribe(r => {
              r[1].changeStamp = jstDate;
            });
          } else if (index % 2 === 0) {
            // 外出
            this.temporaryLeaveTimeStamps.push({
              actual: false,
              date: jstDate,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(jstDate),
              disable: false
            });
          } else if (index % 2 === 1) {
            // 戻り
            this.reenterTimeStamps.push({
              actual: false,
              date: jstDate,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(jstDate),
              disable: false
            });
          }
        }
      });
      this.temporaryLeaveTimeStamps.sort((a, b) => a.date.valueOf() - b.date.valueOf());
      this.reenterTimeStamps.sort((a, b) => a.date.valueOf() - b.date.valueOf());
    }
    this.editable = true;
    this.reflesh();
  }

  compareByValue(f1: any, f2: any) {
    // console.log(`compare a:${f1} b:${f2}`);
    return f1 && f2 && f1.value === f2.value;
  }

  async onSubmit() {
    this.submitting = true;
    await this.request();
    this.dialogRef.close(true);
  }

  async request() {
    const user = await this.auth.getUser();
    const now = new Date();
    // 管理職権限なら自己承認・承認済状態で登録する。
    let state = 0;
    let approverUid = '';
    if (user.authority === Define.Authority.Manager) {
      state = Define.RequestState.Approve;
      approverUid = user.uid;
    } else {
      state = Define.RequestState.Registered;
      approverUid = this.approverUid;
    }
    // console.log('write');
    // console.log(this.keySet.data);

    // await this.afStore.collection('change-request').add(
    //   {
    //     dataVersion: 1, // データバージョン
    //     created: Utility.convertDateToTimestamp(now),
    //     target: Utility.convertDateToTimestamp(this.keySet.date),
    //     uid: user.uid,
    //     timeStamps: timeStamps,
    //     cause: this.keySet.cause,
    //     state: state,
    //     approverUid: approverUid
    //   }
    // );

    const requestRows = this.keySet.data.value;
    const actualStamps = requestRows.map(x => x.actualStamp);
    // firestoreで見た時に 日本標準時(UTC+9)表示で現地時間となるように時差分ずらして登録する。
    const changeStamps = requestRows.map(x => x.changeStamp === null ? null : Utility.convertDateToTimestamp(x.changeStamp));
    const ejectFlags = requestRows.map(x => x.reject);
    await addDoc(collection(this.firestore, 'change-request'), {
      dataVersion: 3, // データバージョン
      created: Timestamp.fromDate(now), // 申請日に時差処理を行うと申請日の比較が出来なくなるため時差処理を行わない。
      target: Utility.convertDateToTimestamp(this.keySet.date),
      uid: user.uid,
      actualStamps,
      changeStamps,
      ejectFlags,
      cause: this.keySet.cause,
      state,
      approverUid
    });

    if (approverUid !== user.approverUid) {
      // 記憶した承認者と異なるならば新しい承認者にDBを更新する。
      await updateDoc(doc(this.firestore, 'users', user.uid), { approverUid });
    }
  }

  isRejectRow(row: RequestInfoSetRecord) {
    if ( row.title === '出社' || row.title === '退社') {
      return false;
    }
    return true;
  }

  openDialog(kind: number) {
    console.log(`openDialog date:${this.keySet.date}`);

    if (this.keySet.date === undefined) {
      console.log('openDialog invalid data.');
      return;
    }
    console.log(`openDialog open`);

    const dialogRef = this.dialog.open(SelectTimeDialogComponent, { data: { data: this.keySet.date } });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`dialog close. result:${result}`);
      const dateDiff = ['前', '', '翌'];
      if (result !== undefined) {
        const dateDiffIndex = Utility.diffDay(this.keySet.date, result) + 1;
        switch (kind + 1) {
          case Define.RequestKindDef.Arrive:
            this.arriveTimeStamps.push({
              actual: false,
              date: result,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(result),
              disable: false
            });
            this.keySet.data.subscribe(records => {
              records[kind].changeStamp = result;
            });
            break;
          case Define.RequestKindDef.Leave:
            this.leaveTimeStamps.push({
              actual: false,
              date: result,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(result),
              disable: false
            });
            this.keySet.data.subscribe(records => {
              records[kind].changeStamp = result;
            });
            break;
          // [index]:[title] [2]:[外出1] [4]:[外出2] [6]:[外出3] [8]:[外出4]
          case Define.RequestKindDef.TemporaryLeave:
            this.temporaryLeaveTimeStamps.push({
              actual: false,
              date: result,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(result),
              disable: false
            });
            this.temporaryLeaveTimeStamps.sort((a, b) => a.date.valueOf() - b.date.valueOf());
            break;
          // [index]:[title] [3]:[戻り1] [5]:[戻り2] [7]:[戻り3] [9]:[戻り4]
          case Define.RequestKindDef.Reenter:
            this.reenterTimeStamps.push({
              actual: false,
              date: result,
              dateText: dateDiff[dateDiffIndex] + Utility.convertDateToMinite(result),
              disable: false
            });
            this.reenterTimeStamps.sort((a, b) => a.date.valueOf() - b.date.valueOf());
            break;
          default:
        }
      }
      this.reflesh();
    });
  }

  remove(timeStamp: TimeStampChip, kind: number) {
    let index: number;
    switch (kind + 1) {
      case Define.RequestKindDef.Arrive:
        index = this.arriveTimeStamps.findIndex(val => val === timeStamp);
        if (index >= 0) {
          this.arriveTimeStamps.splice(index, 1);
          this.keySet.data.subscribe(records => {
            records[kind].changeStamp = null;
          });
        }
        break;
      case Define.RequestKindDef.Leave:
        index = this.leaveTimeStamps.findIndex(val => val === timeStamp);
        if (index >= 0) {
          this.leaveTimeStamps.splice(index, 1);
          this.keySet.data.subscribe(records => {
            records[kind].changeStamp = null;
          });
        }
        break;
      case Define.RequestKindDef.TemporaryLeave:
        index = this.temporaryLeaveTimeStamps.findIndex(val => val === timeStamp);
        if (index >= 0) {
          if (this.temporaryLeaveTimeStamps[index].actual) {
            const actualTimeStamps = this.temporaryLeaveTimeStamps.filter(s => s.actual);
            const actualTimeStampIndex = actualTimeStamps.findIndex(val => val === timeStamp);
            if (actualTimeStampIndex >= 4) {
              // 5つ目以降の打刻の取消はフィールドに定義されていないため不可能
              console.log('eject undefine');
            } else if (!(this.temporaryLeaveTimeStamps[index].disable && this.previewDate[2].length === 4)) {
              this.temporaryLeaveTimeStamps[index].disable = !this.temporaryLeaveTimeStamps[index].disable;
              this.keySet.data.subscribe(records => {
                records[actualTimeStampIndex * 2 + 2].reject = this.temporaryLeaveTimeStamps[index].disable;
              });
            } else {
              // TODO 4つ以上には出来ない旨をユーザーに通知
              console.log('over 4 date item');
            }
          } else {
            this.temporaryLeaveTimeStamps.splice(index, 1);
          }
        }
        break;
      case Define.RequestKindDef.Reenter:
        index = this.reenterTimeStamps.findIndex(val => val === timeStamp);
        if (index >= 0) {
          if (this.reenterTimeStamps[index].actual) {
            const actualTimeStamps = this.reenterTimeStamps.filter(s => s.actual);
            const actualTimeStampIndex = actualTimeStamps.findIndex(val => val === timeStamp);
            if (actualTimeStampIndex >= 4) {
              // 5つ目以降の打刻の取消はフィールドに定義されていないため不可能
              console.log('eject undefine');
            } else if (!(this.reenterTimeStamps[index].disable && this.previewDate[3].length === 4)) {
              this.reenterTimeStamps[index].disable = !this.reenterTimeStamps[index].disable;
              this.keySet.data.subscribe(records => {
                records[actualTimeStampIndex * 2 + 3].reject = this.reenterTimeStamps[index].disable;
              });
            } else {
              // TODO 4つ以上には出来ない旨をユーザーに通知
              console.log('over 4 date item');
            }
          } else {
            this.reenterTimeStamps.splice(index, 1);
          }
        }
        break;
      default:
    }

    this.reflesh();
  }

  onChangeRequestStamp(index: number, value: Date | null) {
    this.keySet.data.subscribe(records => {
      records[index].changeStamp = value ?? null;
    });
    this.reflesh();
  }

  reflesh() {
    this.keySet.data.subscribe(records => {
      const nonActualTemporaryLeaveTimeStamps = this.temporaryLeaveTimeStamps.filter(stamp => !stamp.actual);
      const nonActualReenterTimeStamps = this.reenterTimeStamps.filter(stamp => !stamp.actual);

      this.preview = new Array<string>(4).fill('-');

      this.previewDate = [[], [], [], []];

      if (this.keySet.timeStamps !== undefined) {
        this.keySet.timeStamps.forEach( timeStamp => {
          if (timeStamp.kind === 0 && this.previewDate[0].length === 0) {
            // 出社 最速打刻 (timeStampが日時で昇順となっている前提)
            this.previewDate[0].push(timeStamp.stamp);
          } else if (timeStamp.kind === 1) {
            // 退社 最遅打刻 (timeStampが日時で昇順となっている前提)
            this.previewDate[1] = [timeStamp.stamp];
          } else if (timeStamp.kind === 2) {
            // 外出
            const index = this.temporaryLeaveTimeStamps.findIndex(chip => {
              if (chip.actual) {
                return chip.date === timeStamp.stamp;
              } else {
                return false;
              }
            });
            if (index >= 0) {
              if (!this.temporaryLeaveTimeStamps[index].disable) {
                // 取消フラグが無ければ
                this.previewDate[2].push(timeStamp.stamp);
              }
            }
          } else if (timeStamp.kind === 3) {
            // 戻り
            const index = this.reenterTimeStamps.findIndex(chip => {
              if (chip.actual) {
                return chip.date === timeStamp.stamp;
              } else {
                return false;
              }
            });
            if (index >= 0) {
              if (!this.reenterTimeStamps[index].disable) {
                // 取消フラグが無ければ
                this.previewDate[3].push(timeStamp.stamp);
              }
            }
          }
        });
      }

      records.forEach((r, index) => {
        r.changeStamp = null;
        if (index === 0) {
          // 出社
          if (this.arriveTimeStamps.length !== 0 && this.arriveTimeStamps[0].actual !== true) {
            r.changeStamp = this.arriveTimeStamps[0].date;
            this.previewDate[0] = [this.arriveTimeStamps[0].date];
          }
        } else if (index === 1) {
          // 退社
          if (this.leaveTimeStamps.length !== 0 && this.leaveTimeStamps[0].actual !== true) {
            r.changeStamp = this.leaveTimeStamps[0].date;
            this.previewDate[1] = [this.leaveTimeStamps[0].date];
          }
        } else if (index % 2 === 0) {
          // 外出
          if (nonActualTemporaryLeaveTimeStamps.length !== 0) {
            if (nonActualTemporaryLeaveTimeStamps[(index - 2) / 2] !== undefined
            && nonActualTemporaryLeaveTimeStamps[(index - 2) / 2].date !== null) {
              // [index]:[title] [2]:[外出1] [4]:[外出2] [6]:[外出3] [8]:[外出4]
              r.changeStamp = nonActualTemporaryLeaveTimeStamps[(index - 2) / 2].date;
              this.previewDate[2].push(r.changeStamp);
            }
          }
        } else if (index % 2 === 1) {
          // 戻り
          if (nonActualReenterTimeStamps.length !== 0) {
            if (nonActualReenterTimeStamps[(index - 3) / 2] !== undefined
            && nonActualReenterTimeStamps[(index - 3) / 2].date !== null) {
              // [index]:[title] [3]:[戻り1] [5]:[戻り2] [7]:[戻り3] [9]:[戻り4]
              r.changeStamp = nonActualReenterTimeStamps[(index - 3) / 2].date;
              this.previewDate[3].push(r.changeStamp);
            }
          }
        }
      });

      this.previewDate[2].sort((a, b) => a.valueOf() - b.valueOf());
      this.previewDate[3].sort((a, b) => a.valueOf() - b.valueOf());
      const dateDiff = ['前', '', '翌'];
      this.previewDate.forEach((stamps, index) => {
        stamps.forEach(stamp => {
          const dateDiffIndex = Utility.diffDay(this.keySet.date, stamp) + 1;
          if (this.preview[index] === '-') {
            this.preview[index] = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(stamp);
          } else {
            this.preview[index] += '\n' + dateDiff[dateDiffIndex] + Utility.convertDateToMinite(stamp);
          }
        });
      });
    });
  }
}
